/**
 * The governed computed outputs the platform publishes back to the broker.
 *
 * PRD v0.3 §7.3: the KPI and the constraint verdict are computed in Contextualize and
 * published as their own MQTT topics, and the agent *consumes* the constraint rather than
 * deriving it. This module is the frontend's side of that contract.
 *
 * Why it matters that this exists at all: the Live Line page can work out a constraint and
 * a line rate from the raw tags on its own, and it does when it has to. But a demo whose
 * whole argument is "the platform computes this for you" must not quietly show a
 * browser-side approximation as if it were the platform's answer. So the values are read
 * from the governed topics when they are there, the local derivation is a labelled
 * fallback, and the UI can tell the two apart.
 */

/**
 * Governed UNS root.
 *
 * Identical to `PACKAGING_PREFIX`: the tags and the computed outputs share one root so the
 * topics look like a real plant's. The two writers coexist only because `kpi` and `agent`
 * are reserved segments that the tag parser refuses to read as machines.
 */
export const GOVERNED_PREFIX = 'BBCorp/Lisbon/Packaging'

/** `<line>/kpi/line_performance_rate` */
export const KPI_LINE_PERFORMANCE = 'line_performance_rate'
/** `<line>/<machine>/kpi/performance_rate` */
export const KPI_MACHINE_PERFORMANCE = 'performance_rate'
/** `<line>/kpi/constraint` */
export const KPI_CONSTRAINT = 'constraint'

/** How the value on screen was arrived at. */
export type KpiSource = 'governed' | 'derived'

export interface GovernedLineKpi {
  /** Line performance rate as a percentage, from the platform's own computation. */
  performancePct?: number
  /** Machine id the platform named as the constraint, e.g. `label-01`. */
  constraintAsset?: string
  /** Why, in the platform's vocabulary, e.g. `slow_with_full_infeed`. */
  constraintReason?: string
  /** Rolling window the KPI was computed over, in seconds. */
  windowSeconds?: number
  /** Per-machine performance rate, keyed by machine id. */
  machinePerformancePct: Record<string, number>
  /** Newest `computed_at` seen for this line, as epoch ms. */
  computedAtMs?: number
}

export type GovernedKpiByLine = Record<string, GovernedLineKpi>

/**
 * A governed value is only trusted while it is fresh.
 *
 * The PRD publishes computed outputs every 60 seconds, so three missed publishes is the
 * point at which the platform has clearly stopped computing and the page should say so
 * rather than keep showing a stale 80%.
 */
export const GOVERNED_STALE_AFTER_MS = 180_000

interface ComputedPayload {
  value?: unknown
  window_s?: unknown
  constraint_asset?: unknown
  asset?: unknown
  reason?: unknown
  computed_at?: unknown
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined
}

function asEpochMs(v: unknown): number | undefined {
  const s = asString(v)
  if (!s) {
    return undefined
  }
  const parsed = Date.parse(s)
  return Number.isNaN(parsed) ? undefined : parsed
}

/**
 * Parse one governed topic into the line it belongs to and what it carries.
 *
 * Returns null for anything that is not a §7.3 computed output, so the caller can pass a
 * whole flush through without pre-filtering.
 */
export function parseGovernedTopic(
  topic: string,
  prefix: string = GOVERNED_PREFIX,
): { line: string; kind: 'line' | 'machine' | 'constraint'; machine?: string } | null {
  const root = prefix.replace(/\/+$/, '')
  if (!topic.startsWith(`${root}/`)) {
    return null
  }
  const parts = topic.slice(root.length + 1).split('/')

  // <line>/kpi/<leaf>
  if (parts.length === 3 && parts[1] === 'kpi') {
    if (parts[2] === KPI_LINE_PERFORMANCE) {
      return { line: parts[0], kind: 'line' }
    }
    if (parts[2] === KPI_CONSTRAINT) {
      return { line: parts[0], kind: 'constraint' }
    }
    return null
  }

  // <line>/<machine>/kpi/<leaf>
  if (parts.length === 4 && parts[2] === 'kpi' && parts[3] === KPI_MACHINE_PERFORMANCE) {
    return { line: parts[0], kind: 'machine', machine: parts[1] }
  }

  return null
}

/**
 * Fold a flush of MQTT values into the governed KPI per line.
 *
 * `values` is whatever `useMqttTopicValues` last held; entries that are not §7.3 outputs
 * are ignored. Payloads are the full computed shape, not the bare `{value, timestamp}` of
 * the tag branches, so `value` is read out of the already-parsed object.
 */
export function buildGovernedKpi(
  values: Record<string, { value: unknown; payload?: Record<string, unknown> }>,
  prefix: string = GOVERNED_PREFIX,
): GovernedKpiByLine {
  const byLine: GovernedKpiByLine = {}

  const forLine = (line: string): GovernedLineKpi => {
    const existing = byLine[line]
    if (existing) {
      return existing
    }
    const fresh: GovernedLineKpi = { machinePerformancePct: {} }
    byLine[line] = fresh
    return fresh
  }

  for (const [topic, entry] of Object.entries(values)) {
    const parsed = parseGovernedTopic(topic, prefix)
    if (!parsed) {
      continue
    }

    // Read the whole decoded object, not just `value`: the computed outputs carry
    // `window_s`, `constraint_asset` and `computed_at` alongside it, and those are the
    // fields that make the value attributable.
    const payload = (entry.payload ??
      (entry.value !== null && typeof entry.value === 'object'
        ? entry.value
        : { value: entry.value })) as ComputedPayload

    const target = forLine(parsed.line)
    const computedAt = asEpochMs(payload.computed_at)
    if (
      computedAt !== undefined &&
      (target.computedAtMs === undefined || computedAt > target.computedAtMs)
    ) {
      target.computedAtMs = computedAt
    }

    if (parsed.kind === 'line') {
      target.performancePct = asNumber(payload.value)
      target.windowSeconds = asNumber(payload.window_s)
      // The line KPI payload also names the constraint, per §7.3. The dedicated
      // constraint topic wins if both are present, so this only fills a gap.
      target.constraintAsset = target.constraintAsset ?? asString(payload.constraint_asset)
      continue
    }

    if (parsed.kind === 'constraint') {
      target.constraintAsset = asString(payload.asset) ?? target.constraintAsset
      target.constraintReason = asString(payload.reason)
      continue
    }

    if (parsed.machine) {
      const pct = asNumber(payload.value)
      if (pct !== undefined) {
        target.machinePerformancePct[parsed.machine] = pct
      }
    }
  }

  return byLine
}

/**
 * Is this line's governed KPI usable right now?
 *
 * A line with no `computed_at` at all is treated as usable if it carries a value, since a
 * platform that omits the timestamp is still publishing; only a timestamp that is
 * demonstrably old disqualifies it.
 */
export function isGovernedFresh(
  kpi: GovernedLineKpi | undefined,
  nowMs: number,
  staleAfterMs: number = GOVERNED_STALE_AFTER_MS,
): boolean {
  if (!kpi) {
    return false
  }
  const hasValue =
    kpi.performancePct !== undefined ||
    kpi.constraintAsset !== undefined ||
    Object.keys(kpi.machinePerformancePct).length > 0
  if (!hasValue) {
    return false
  }
  if (kpi.computedAtMs === undefined) {
    return true
  }
  return nowMs - kpi.computedAtMs <= staleAfterMs
}

/** Topic filters to subscribe to for the governed outputs of one site/area. */
export function governedTopicFilters(prefix: string = GOVERNED_PREFIX): string[] {
  const root = prefix.replace(/\/+$/, '')
  // Narrow filters for a consumer that wants only the computed outputs. The Live Line
  // page does not use these - it binds `<root>/#` once, because the tags and the computed
  // outputs now share that root - but an agent or a dashboard reading only the KPI should
  // not have the whole tag tree pushed at it.
  return [`${root}/+/kpi/+`, `${root}/+/+/kpi/+`]
}
